/**
 * components/OtpInput.tsx — 6-box one-time code input.
 *
 * Behaviour:
 *   * Six single-character boxes. On input, focus advances to the
 *     next box. On backspace on an empty box, focus retreats to the
 *     previous box.
 *   * Paste anywhere in the row distributes the digits into all
 *     boxes (up to the 6-slot limit) and moves focus to the first
 *     empty slot (or the last box if all are full).
 *   * Digits only. Any non-digit is silently dropped.
 *   * When all six boxes are filled, `onComplete(code)` fires — the
 *     parent typically kicks the verify request from that callback.
 *   * Setting `disabled` locks the boxes (used during the verify
 *     network round-trip).
 *   * Setting `error` renders each box with the danger border and
 *     shakes the row (respecting prefers-reduced-motion).
 *
 * Accessibility:
 *   * Each box has `inputMode="numeric"` and `autoComplete="one-time-code"`
 *     so iOS/Android surface SMS-autofill on the first box.
 *   * The row is labelled via `aria-labelledby` when the parent
 *     provides `labelId`.
 *   * `aria-invalid` and `aria-describedby` fire when `error` is set.
 */

import { useEffect, useRef, useState } from "react";
import styles from "./OtpInput.module.css";

interface OtpInputProps {
  value: string;
  onChange: (next: string) => void;
  onComplete?: (code: string) => void;
  disabled?: boolean;
  error?: boolean;
  autoFocus?: boolean;
  /** Optional id for the group; used by aria-describedby / aria-labelledby. */
  id?: string;
  labelId?: string;
  errorId?: string;
}

const LENGTH = 6;

export function OtpInput({
  value,
  onChange,
  onComplete,
  disabled = false,
  error = false,
  autoFocus = true,
  id,
  labelId,
  errorId,
}: OtpInputProps) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const [shake, setShake] = useState(false);

  useEffect(() => {
    if (autoFocus && !disabled) {
      refs.current[0]?.focus();
    }
  }, [autoFocus, disabled]);

  // Shake animation when `error` transitions true → false.
  useEffect(() => {
    if (error) {
      setShake(true);
      const t = setTimeout(() => setShake(false), 420);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [error]);

  const digits = value.padEnd(LENGTH, " ").slice(0, LENGTH).split("");

  function commit(next: string) {
    // Normalise: drop non-digits, cap length at 6.
    const clean = next.replace(/\D+/g, "").slice(0, LENGTH);
    onChange(clean);
    if (clean.length === LENGTH && onComplete) onComplete(clean);
  }

  function onBoxChange(idx: number, raw: string) {
    // Grab the LAST non-digit-filtered char the user typed. Handles
    // browsers that pass along a whole IME string.
    const chars = raw.replace(/\D+/g, "");
    if (!chars) return;
    const arr = value.split("");
    // Ensure we have `idx` slots before writing (pad with existing).
    while (arr.length < idx) arr.push("");
    arr[idx] = chars[chars.length - 1];  // last typed
    const next = arr.join("").slice(0, LENGTH);
    commit(next);
    // Advance focus.
    const nextIdx = Math.min(idx + 1, LENGTH - 1);
    refs.current[nextIdx]?.focus();
    refs.current[nextIdx]?.select();
  }

  function onKeyDown(idx: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace") {
      // If the current box has a digit, clear it and stay. If empty,
      // clear the previous box and retreat.
      if (value[idx]) {
        e.preventDefault();
        const arr = value.split("");
        arr[idx] = "";
        commit(arr.join(""));
        return;
      }
      if (idx > 0) {
        e.preventDefault();
        const arr = value.split("");
        arr[idx - 1] = "";
        commit(arr.join(""));
        refs.current[idx - 1]?.focus();
        refs.current[idx - 1]?.select();
      }
    } else if (e.key === "ArrowLeft" && idx > 0) {
      e.preventDefault();
      refs.current[idx - 1]?.focus();
      refs.current[idx - 1]?.select();
    } else if (e.key === "ArrowRight" && idx < LENGTH - 1) {
      e.preventDefault();
      refs.current[idx + 1]?.focus();
      refs.current[idx + 1]?.select();
    }
  }

  function onPaste(e: React.ClipboardEvent) {
    const pasted = e.clipboardData.getData("text");
    const chars = pasted.replace(/\D+/g, "").slice(0, LENGTH);
    if (!chars) return;
    e.preventDefault();
    commit(chars);
    // Focus first empty slot, or the last box if all full.
    const first = chars.length >= LENGTH ? LENGTH - 1 : chars.length;
    refs.current[first]?.focus();
    refs.current[first]?.select();
  }

  return (
    <div
      id={id}
      className={`${styles.row} ${shake ? styles.shake : ""}`}
      role="group"
      aria-labelledby={labelId}
      aria-describedby={error && errorId ? errorId : undefined}
      onPaste={onPaste}
    >
      {digits.map((ch, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={1}
          className={
            error
              ? `${styles.box} ${styles.boxError}`
              : styles.box
          }
          value={ch === " " ? "" : ch}
          onChange={(e) => onBoxChange(i, e.target.value)}
          onKeyDown={(e) => onKeyDown(i, e)}
          onFocus={(e) => e.target.select()}
          disabled={disabled}
          aria-label={`Digit ${i + 1} of ${LENGTH}`}
          aria-invalid={error || undefined}
        />
      ))}
    </div>
  );
}

export const OTP_LENGTH = LENGTH;
